import React from 'react'
import { Box, Paper, Divider, Typography, makeStyles } from '@material-ui/core'
import { FieldArray } from 'react-final-form-arrays'
import MyButton from '../../../../control/button/button'
import useWebService from '../../../../services/web/useWebService'
import useAuthenStore from '../../../../stores/authen/authenStore'
import AgendaCard from './AgendaCard'
import { ProjectSearchFn } from '../agendaTypes'
import useTranslate from '../../../../i18n/useTranslate'

const MeetingAgendaItemsForm: React.FC<{ prefix?: string; canEdit?: boolean }> = ({
    prefix = 'agendaItems',
    canEdit = true,
}) => {
    const classes = useStyles()
    const t = useTranslate()
    const { searchTblMeeting: searchTblMeetingApi } = useWebService()
    const profile = useAuthenStore()?.profile

    const searchProjects = React.useCallback<ProjectSearchFn>(
        (payload, config) =>
            searchTblMeetingApi(
                {
                    ...payload,
                    userId: profile?.userId ?? payload.userId,
                },
                profile.userGroupId + '',
                config,
            ),
        [searchTblMeetingApi, profile?.userId],
    )

    return (
        <Paper className={classes.root} elevation={0}>
            <Box className={classes.headerBar}>
                <Typography variant="h6" style={{ fontWeight: 700 }}>
                    {t.get('UM.SETTING_AGENDA.AGENDA_ITEMS.HEADER')}
                </Typography>
                <FieldArray name={`${prefix}.agendas`}>
                    {({ fields }) => (
                        <MyButton
                            type="button"
                            color="primary"
                            disabled={!canEdit}
                            onClick={() => fields.push({ title: '', detail: '', projects: [], subs: [] })}
                        >
                            {t.get('UM.SETTING_AGENDA.AGENDA_ITEMS.ADD_AGENDA')}
                        </MyButton>
                    )}
                </FieldArray>
            </Box>

            <Divider />

            <Box className={classes.section}>
                <FieldArray name={`${prefix}.agendas`}>
                    {({ fields, meta }) => {
                        const count =
                            (fields as any)?.length ??
                            (Array.isArray((fields as any)?.value) ? (fields as any).value.length : 0)
                        return (
                            <>
                                {fields.map((pfx: string, idx: number) => (
                                    <AgendaCard
                                        key={pfx}
                                        idx={idx}
                                        namePrefix={pfx}
                                        onRemove={() => fields.remove(idx)}
                                        searchTbl={searchProjects}
                                        canEdit={canEdit}
                                    />
                                ))}
                                {meta.error && typeof meta.error === 'string' && (
                                    <Typography className={classes.errorText}>{meta.error}</Typography>
                                )}
                                {count === 0 && (
                                    <Typography className={classes.errorText}>
                                        {t.get('UM.SETTING_AGENDA.AGENDA_ITEMS.EMPTY_AGENDA')}
                                    </Typography>
                                )}
                            </>
                        )
                    }}
                </FieldArray>
            </Box>
        </Paper>
    )
}

const useStyles = makeStyles((theme) => ({
    root: { borderRadius: 12, border: '1px solid #E5E7EB', background: '#fff' },
    headerBar: {
        padding: theme.spacing(3),
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 16,
        fontWeight: 700,
    },
    section: { padding: theme.spacing(3), display: 'flex', flexDirection: 'column', gap: 16 },
    errorText: { color: '#d32f2f', fontSize: 12, marginTop: 4 },
}))

export default MeetingAgendaItemsForm
